import { CheckCircle, HourglassLow, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { RegisterLink } from '@/components/RegisterLink/RegisterLink';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryCatalogOnly,
    getItemSummaryIsAvailable,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { GlobalState } from '@/redux/store';
type Props = {
    catalogId: number;
    itemId: number;
};

const CardCtaBanner = (props: Props) => {
    const { catalogId, itemId } = props;
    const isCatalogOnly = useAppSelector((state: GlobalState) => getItemSummaryCatalogOnly(state, itemId));
    const isAvailable = useAppSelector((state: GlobalState) => getItemSummaryIsAvailable(state, itemId));
    const {
        approved = false,
        declined = false,
        pending = false,
        registered = false,
    } = useAppSelector((state) => getCatalogRegistration(state, catalogId));

    if (!isAvailable) {
        return null;
    } else if (approved) {
        return (
            <ApprovedDiv>
                <CheckCircle
                    size="xs"
                    style="fill"
                />
                <FormattedMessage id="approved_to_bid" />
            </ApprovedDiv>
        );
    } else if (declined) {
        return (
            <DeclinedDiv>
                <Warning
                    color="critical"
                    size="sm"
                    style="fill"
                />
                <FormattedMessage id="registration_status_label.registration_declined" />
            </DeclinedDiv>
        );
    } else if (pending && isCatalogOnly) {
        return (
            <PendingDiv>
                <HourglassLow
                    size="sm"
                    style="fill"
                />
                <FormattedMessage id="registration_status_label.catalog_only_registration_submitted" />
            </PendingDiv>
        );
    } else if (pending) {
        return (
            <PendingDiv>
                <HourglassLow
                    size="sm"
                    style="fill"
                />
                <FormattedMessage id="registration_status_label.registration_pending" />
            </PendingDiv>
        );
    } else if (!registered) {
        return (
            <RegisterDiv>
                <StyledRegisterLink catalogId={catalogId} />
            </RegisterDiv>
        );
    }

    return null;
};

export default React.memo<Props>(CardCtaBanner);

const BannerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 26px;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;

const ApprovedDiv = styled.div`
    ${BannerStyle}
    color: ${({ theme }) => theme.colors.teal};
    & > span {
        word-break: keep-all;
        display: inline-block;
        height: 26px;
        overflow: hidden;
        line-height: 26px;
        width: 90px;
    }
    & > svg {
        margin-right: 8px;
    }
`;

const DeclinedDiv = styled.div`
    ${BannerStyle}
    color: ${({ theme }) => theme.colors.red100};
    & > svg {
        margin-right: 8px;
    }
`;

const PendingDiv = styled.div`
    ${BannerStyle}
    color: ${({ theme }) => theme.colors.grey100};
    & > span {
        word-break: keep-all;
        height: 26px;
        overflow: hidden;
        line-height: 26px;
        width: 74px;
        display: flex;
        flex-direction: column-reverse;
    }

    & > svg {
        margin-right: 8px;
    }
`;

const RegisterDiv = styled.div`
    ${BannerStyle}
    .register-link {
        text-transform: none;
    }
    & > svg {
        margin-right: 8px;
    }
`;

const StyledRegisterLink = styled(RegisterLink)`
    text-transform: none;
`;
